import { useEffect, useState } from 'react';
import { Plus, FileText, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const STATUS_CHIP = {
    submitted: 'bg-amber-100 text-amber-700',
    approved:  'bg-blue-100 text-blue-700',
    paid:      'bg-emerald-100 text-emerald-700',
    rejected:  'bg-rose-100 text-rose-700',
    cancelled: 'bg-ink-100 text-ink-700',
};

export default function SalesReimbursements() {
    const [items, setItems] = useState([]);
    const [openNew, setOpenNew] = useState(false);
    const load = () => api.get('/reimbursements/me').then(r => setItems(r.data.items));
    useEffect(() => { load(); }, []);

    const cancel = async (r) => {
        if (!confirm('Cancel this claim?')) return;
        await api.post(`/reimbursements/${r._id}/cancel`);
        toast.success('Cancelled');
        load();
    };

    return (
        <div className="space-y-4">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-2xl font-extrabold">My reimbursements</h1>
                    <p className="text-sm text-ink-500">Submit expenses with proof. Admin will review.</p>
                </div>
                <button className="ag-btn-primary" onClick={() => setOpenNew(true)}><Plus className="h-4 w-4" /> Submit expense</button>
            </div>
            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>Submitted</th><th>Title</th><th>Category</th><th>Method</th><th>Vendor</th><th>Amount</th><th>Proof</th><th>Status</th><th>Note</th><th></th>
                    </tr></thead>
                    <tbody>
                        {items.map(r => (
                            <tr key={r._id}>
                                <td className="text-xs text-ink-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                                <td className="font-medium">{r.title}</td>
                                <td className="text-xs uppercase tracking-wider text-ink-500">{r.category}</td>
                                <td className="text-xs uppercase tracking-wider text-ink-500">{r.paymentMethod || '—'}</td>
                                <td className="text-xs">{r.vendor || '—'}</td>
                                <td>₹ {r.amountINR.toLocaleString('en-IN')}</td>
                                <td className="flex gap-2 py-2.5">
                                    {r.screenshotUrl && <a href={r.screenshotUrl} target="_blank" rel="noreferrer" title="Screenshot" className="text-ink-500 hover:text-brand-teal"><ImageIcon className="h-4 w-4" /></a>}
                                    {r.billUrl && <a href={r.billUrl} target="_blank" rel="noreferrer" title="Bill" className="text-ink-500 hover:text-brand-teal"><FileText className="h-4 w-4" /></a>}
                                    {!r.screenshotUrl && !r.billUrl && <span className="text-xs text-ink-500">—</span>}
                                </td>
                                <td><span className={`ag-chip ${STATUS_CHIP[r.status]}`}>{r.status}</span></td>
                                <td className="text-xs text-ink-500">{r.reviewNote || '—'}</td>
                                <td>
                                    {r.status === 'submitted' && <button className="ag-btn-ghost text-xs" onClick={() => cancel(r)}>Cancel</button>}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={10} className="text-center text-ink-500 py-8">No claims yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            {openNew && <NewExpenseModal onClose={(s) => { setOpenNew(false); if (s) load(); }} />}
        </div>
    );
}

function NewExpenseModal({ onClose }) {
    const [form, setForm] = useState({
        title: '', reason: '', description: '', notes: '',
        category: 'travel', paymentMethod: 'upi', vendor: '', bankDetails: '',
        amountINR: 0,
        incurredOn: new Date().toISOString().slice(0, 10),
    });
    const [screenshot, setScreenshot] = useState(null);
    const [bill, setBill] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title || form.amountINR <= 0) return toast.error('Title and amount > 0 required');
        setBusy(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (screenshot) fd.append('screenshot', screenshot);
            if (bill)       fd.append('bill', bill);
            await api.post('/reimbursements', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Submitted for review');
            onClose(true);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 grid place-items-center p-4" onClick={() => onClose(false)}>
            <form onSubmit={submit} className="bg-white rounded-xl2 shadow-soft w-full max-w-2xl p-6 space-y-3 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold text-lg">Submit expense</h3>

                <Field label="Title *"   value={form.title}  onChange={v => setForm({...form, title: v})} required placeholder="e.g. Client meeting cab" />
                <Field label="Reason *"  value={form.reason} onChange={v => setForm({...form, reason: v})} required placeholder="Purpose / business reason" />

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Select label="Category" value={form.category} onChange={v => setForm({...form, category: v})}
                            options={['travel','meal','fuel','lodging','communication','event','office_supply','other']} />
                    <Select label="Payment" value={form.paymentMethod} onChange={v => setForm({...form, paymentMethod: v})}
                            options={['cash','upi','card','netbanking','wallet','other']} />
                    <Field label="Amount ₹ *" type="number" value={form.amountINR} onChange={v => setForm({...form, amountINR: Number(v)})} required />
                    <Field label="Date" type="date" value={form.incurredOn} onChange={v => setForm({...form, incurredOn: v})} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Vendor" value={form.vendor} onChange={v => setForm({...form, vendor: v})} placeholder="who you paid" />
                    <Field label="Bank details" value={form.bankDetails} onChange={v => setForm({...form, bankDetails: v})} placeholder="for reimbursement payout" />
                </div>

                <div>
                    <label className="ag-label">Description</label>
                    <textarea className="ag-input min-h-[70px]" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                </div>
                <div>
                    <label className="ag-label">Notes</label>
                    <textarea className="ag-input min-h-[60px]" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <FileSlot label="Screenshot / receipt photo" file={screenshot} onChange={setScreenshot} />
                    <FileSlot label="Bill / invoice (PDF or image)" file={bill} onChange={setBill} />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                    <button className="ag-btn-primary" disabled={busy}>{busy ? 'Submitting…' : 'Submit'}</button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, type='text', required, placeholder }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <input className="ag-input" type={type} required={required} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
        </div>
    );
}
function Select({ label, value, onChange, options }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <select className="ag-input" value={value} onChange={e => onChange(e.target.value)}>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
function FileSlot({ label, file, onChange }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <label className="ag-input flex items-center justify-between cursor-pointer text-xs">
                <span className="truncate">{file ? file.name : 'Choose file…'}</span>
                <input type="file" className="hidden" accept="image/*,.pdf" onChange={e => onChange(e.target.files?.[0] || null)} />
                <span className="ag-btn-ghost text-[11px] py-1 px-2">Browse</span>
            </label>
        </div>
    );
}
